import { CHAPERON_API_URL } from "../const/paths"
import { consoleLog } from "../utilities/helpers"

function getAuthHeaders() {
    const token = localStorage.getItem("chaperon_auth_token")
    if (!token) {
        const error = new Error("Authentication required. Please log in.")
        error.status = 401
        throw error
    }

    return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
    }
}

async function checkerRequest(endpoint, { method = "GET", data } = {}) {
    const response = await fetch(`${CHAPERON_API_URL}${endpoint}`, {
        method,
        headers: getAuthHeaders(),
        body: data ? JSON.stringify(data) : undefined,
    })

    const contentType = response.headers.get("content-type") || ""
    const isJson = contentType.includes("application/json")
    const result = isJson ? await response.json() : null

    if (!response.ok) {
        const message =
            result?.message || result?.error || "Checker request failed"
        const error = new Error(message)
        error.status = response.status
        error.response = result
        throw error
    }

    return result
}

function buildQuery(params = {}) {
    const query = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            query.set(key, String(value))
        }
    })

    const queryString = query.toString()
    return queryString ? `?${queryString}` : ""
}

export async function getCheckerTrips({ user_id, status, limit = 50 } = {}) {
    consoleLog("getCheckerTrips:", { user_id, status, limit })
    const query = buildQuery({ user_id, status, limit })
    return checkerRequest(`/v1/checker/get${query}`)
}

export async function getCheckerTrip(id) {
    consoleLog("getCheckerTrip:", id)
    return checkerRequest(`/v1/checker/${id}`)
}

export async function createCheckerTrip(payload) {
    consoleLog("createCheckerTrip:", payload)
    return checkerRequest("/v1/checker/create", {
        method: "POST",
        data: payload,
    })
}

export async function updateCheckerTrip(id, payload) {
    consoleLog("updateCheckerTrip:", { id, payload })
    return checkerRequest(`/v1/checker/${id}`, {
        method: "PATCH",
        data: payload,
    })
}

export async function extendCheckerTrip(id, extension_minutes) {
    consoleLog("extendCheckerTrip:", { id, extension_minutes })
    return checkerRequest(`/v1/checker/${id}/extend`, {
        method: "POST",
        data: { extension_minutes },
    })
}

export async function cancelCheckerTrip(id) {
    consoleLog("cancelCheckerTrip:", id)
    return checkerRequest(`/v1/checker/${id}/cancel`, {
        method: "POST",
    })
}

export async function reuseCheckerTrip(id, payload = {}) {
    consoleLog("reuseCheckerTrip:", { id, payload })
    return checkerRequest(`/v1/checker/${id}/reuse`, {
        method: "POST",
        data: payload,
    })
}

export async function submitCheckerCheckin(id, payload) {
    consoleLog("submitCheckerCheckin:", { id, payload })
    return checkerRequest(`/v1/checker/${id}/checkin`, {
        method: "POST",
        data: payload,
    })
}

export async function getCheckerEvents(id) {
    consoleLog("getCheckerEvents:", id)
    return checkerRequest(`/v1/checker/${id}/events`)
}

/**
 * Owner-only: activates a scheduled trip early. The card fires this itself
 * the instant its own countdown to start_time hits zero (see
 * useCheckerCountdown), same as the Android app's auto-activate; the server
 * treats an already-active trip as success rather than an error, so a race
 * with another device is harmless.
 */
export async function activateCheckerTrip(id) {
    consoleLog("activateCheckerTrip:", id)
    return checkerRequest(`/v1/checker/${id}/activate`, { method: "POST" })
}

/**
 * Owner-only, hard delete. Only a draft/scheduled trip (no check-in history
 * yet) can be deleted outright; an active trip must go through
 * cancelCheckerTrip instead (see EditTripScreen's same distinction).
 */
export async function deleteCheckerTrip(id) {
    consoleLog("deleteCheckerTrip:", id)
    return checkerRequest(`/v1/checker/${id}`, { method: "DELETE" })
}

/**
 * Owner-only: invite a community member to a trip's convoy.
 * @param {number|string} id - Checker trip id
 * @param {number|string} companionUserId
 */
export async function addCheckerCompanion(id, companionUserId) {
    consoleLog("addCheckerCompanion:", { id, companionUserId })
    return checkerRequest(`/v1/checker/${id}/companions/add`, {
        method: "POST",
        data: { companion_user_id: companionUserId },
    })
}

/**
 * Owner-only: remove a companion from a trip's convoy.
 * @param {number|string} id - Checker trip id
 * @param {number|string} companionUserId
 */
export async function removeCheckerCompanion(id, companionUserId) {
    consoleLog("removeCheckerCompanion:", { id, companionUserId })
    return checkerRequest(`/v1/checker/${id}/companions/remove`, {
        method: "POST",
        data: { companion_user_id: companionUserId },
    })
}

/**
 * The owner's own row plus every invited/accepted/declined/removed
 * companion, each with first_name/last_name/username/profile_picture_url
 * joined in.
 */
export async function getCheckerCompanions(id) {
    consoleLog("getCheckerCompanions:", id)
    return checkerRequest(`/v1/checker/${id}/companions`)
}

/**
 * A companion accepts or declines their own invitation. Companions may only
 * respond on their own behalf, so this never takes a target user id.
 * @param {number|string} id - Checker trip id
 * @param {"accepted"|"declined"} status
 */
export async function respondCheckerCompanion(id, status) {
    consoleLog("respondCheckerCompanion:", { id, status })
    return checkerRequest(`/v1/checker/${id}/companions/respond`, {
        method: "POST",
        data: { status },
    })
}

/**
 * An accepted companion nudges the owner with a safety check-in reminder.
 * Owner-facing only when the trip is actually overdue or in danger.
 */
export async function pingCheckerTrip(id) {
    consoleLog("pingCheckerTrip:", id)
    return checkerRequest(`/v1/checker/${id}/companions/ping`, {
        method: "POST",
    })
}

/**
 * An accepted companion stops watching a trip they no longer want to
 * monitor. Self-service equivalent of the owner removing them.
 */
export async function leaveCheckerTrip(id) {
    consoleLog("leaveCheckerTrip:", id)
    return checkerRequest(`/v1/checker/${id}/companions/leave`, {
        method: "POST",
    })
}

/**
 * Trips the caller has been invited to, or accepted, as a companion --
 * the Convoy page's data source (GET /v1/checker/convoy).
 * @param {Object} [options]
 * @param {number|string} [options.limit]
 */
export async function getConvoyTrips({ limit } = {}) {
    consoleLog("getConvoyTrips:", { limit })
    const query = buildQuery({ limit })
    return checkerRequest(`/v1/checker/convoy${query}`)
}
